import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import RichTextEditor from '@/components/ui/RichTextEditor';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, UploadCloud } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';
import { Save, Trash2, FilePlus2 } from 'lucide-react';
import { useProfile } from '@/hooks/useProfile';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { v4 as uuidv4 } from 'uuid';

const MarketingSettings = () => {
  const [subject, setSubject] = useState('');
  const [headline, setHeadline] = useState('');
  const [headerImageUrl, setHeaderImageUrl] = useState('');
  const [bodyContent, setBodyContent] = useState('');
  const [ctaText, setCtaText] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [blockedEmailsText, setBlockedEmailsText] = useState('');
  const [templates, setTemplates] = useState<Array<{
    id: string;
    name: string;
    subject: string | null;
    headline: string | null;
    header_image_url: string | null;
    body_content: string;
    cta_text: string | null;
    cta_url: string | null;
  }>>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [templateName, setTemplateName] = useState<string>('');
  
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [audience, setAudience] = useState('all');
  const { toast } = useToast();
  const { profile } = useProfile();

  useEffect(() => {
    if (profile?.id) {
      loadTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  const loadTemplates = async () => {
    const { data, error } = await supabase
      .from('marketing_emails')
      .select('*')
      .eq('user_id', profile?.id as string)
      .order('updated_at', { ascending: false });
    if (error) {
      console.error('Error loading templates:', error);
      toast({ title: 'Error', description: 'Failed to load saved emails.', variant: 'destructive' });
      return;
    }
    setTemplates((data || []) as any);
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      toast({ title: 'Name required', description: 'Please enter a name for the saved email.', variant: 'destructive' });
      return;
    }
    const { error } = await supabase
      .from('marketing_emails')
      .insert({
        user_id: profile?.id,
        name: templateName.trim(),
        subject,
        headline,
        header_image_url: headerImageUrl || null,
        body_content: bodyContent,
        cta_text: ctaText || null,
        cta_url: ctaUrl || null,
      });
    if (error) {
      console.error('Error saving template:', error);
      toast({ title: 'Error', description: 'Failed to save email.', variant: 'destructive' });
      return;
    }
    toast({ title: 'Saved', description: 'Email saved successfully.' });
    setTemplateName('');
    await loadTemplates();
  };

  const handleUpdateTemplate = async () => {
    if (!selectedTemplateId) {
      toast({ title: 'No template selected', description: 'Select a template to update.', variant: 'destructive' });
      return;
    }
    const { error } = await supabase
      .from('marketing_emails')
      .update({
        name: templateName.trim() || undefined,
        subject,
        headline,
        header_image_url: headerImageUrl || null,
        body_content: bodyContent,
        cta_text: ctaText || null,
        cta_url: ctaUrl || null,
      })
      .eq('id', selectedTemplateId)
      .eq('user_id', profile?.id as string);
    if (error) {
      console.error('Error updating template:', error);
      toast({ title: 'Error', description: 'Failed to update email.', variant: 'destructive' });
      return;
    }
    toast({ title: 'Updated', description: 'Email updated successfully.' });
    await loadTemplates();
  };

  const handleDeleteTemplate = async () => {
    if (!selectedTemplateId) {
      toast({ title: 'No template selected', description: 'Select a template to delete.', variant: 'destructive' });
      return;
    }
    const { error } = await supabase
      .from('marketing_emails')
      .delete()
      .eq('id', selectedTemplateId)
      .eq('user_id', profile?.id as string);
    if (error) {
      console.error('Error deleting template:', error);
      toast({ title: 'Error', description: 'Failed to delete saved email.', variant: 'destructive' });
      return;
    }
    toast({ title: 'Deleted', description: 'Saved email deleted.' });
    setSelectedTemplateId('');
    setTemplateName('');
    await loadTemplates();
  };

  const applyTemplateToForm = () => {
    if (!selectedTemplateId) return;
    const tpl = templates.find(t => t.id === selectedTemplateId);
    if (!tpl) return;
    setTemplateName(tpl.name || '');
    setSubject(tpl.subject || '');
    setHeadline(tpl.headline || '');
    setHeaderImageUrl(tpl.header_image_url || '');
    setBodyContent(tpl.body_content || '');
    setCtaText(tpl.cta_text || '');
    setCtaUrl(tpl.cta_url || '');
    toast({ title: 'Loaded', description: 'Template applied to the form.' });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    } else {
      setSelectedFile(null);
    }
  };

  const handleUploadImage = async () => {
    if (!selectedFile) {
      toast({
        title: 'No file selected',
        description: 'Please select an image file to upload.',
        variant: 'destructive',
      });
      return;
    }

    setUploadingImage(true);
    try {
      const fileExtension = selectedFile.name.split('.').pop();
      const fileName = `${uuidv4()}.${fileExtension}`;
      const filePath = `marketing/${fileName}`;

      const { data, error } = await supabase.storage
        .from('marketing-images')
        .upload(filePath, selectedFile, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) {
        throw error;
      }

      const { data: publicUrlData } = supabase.storage
        .from('marketing-images')
        .getPublicUrl(filePath);

      if (publicUrlData.publicUrl) {
        setHeaderImageUrl(publicUrlData.publicUrl);
        setSelectedFile(null);
        toast({
          title: 'Image Uploaded',
          description: 'Header image uploaded successfully.',
        });
      } else {
        throw new Error('Could not get public URL for the uploaded image.');
      }

    } catch (error: any) {
      console.error('Error uploading image:', error);
      toast({
        title: 'Image Upload Failed',
        description: error.message || 'An unknown error occurred during upload.',
        variant: 'destructive',
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSendEmails = async () => {
    if (audience === 'test' && !profile?.email) {
      toast({
        title: 'Cannot Send Test',
        description: 'Your user profile email is not available for a test send.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    try {
      const body = {
        subject,
        headline,
        bodyContent,
        headerImageUrl,
        ctaText,
        ctaUrl,
        audience: audience === 'test' ? undefined : audience,
        testEmailAddress: audience === 'test' ? profile?.email : undefined,
        blockedEmails: blockedEmailsText
          .split(/[\s,]+/)
          .map((e) => e.trim())
          .filter((e) => !!e),
      };

      if (audience === 'test') {
        delete body.audience;
      } else {
        delete body.testEmailAddress;
      }

      const { data, error } = await supabase.functions.invoke('send-marketing-email', {
        body,
      });

      if (error) throw error;

      if (audience === 'test') {
        toast({
          title: 'Test Email Sent',
          description: `A test email has been sent to ${profile?.email}.`,
        });
      } else {
        toast({
          title: 'Email blast sent!',
          description: `Marketing email was sent to ${data.sentCount} users.`,
        });
      }
      // Clear fields after sending
      setSubject('');
      setHeadline('');
      setHeaderImageUrl('');
      setBodyContent('');
      setCtaText('');
      setCtaUrl('');
      setBlockedEmailsText('');

    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'An unknown error occurred.';
      toast({
        title: 'Error Sending Emails',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const audienceLabels = {
    all: 'All Users',
    paying: 'Paying Subscribers',
    free: 'Free Users',
    investors: 'Investors',
    affiliates: 'Affiliates',
    admin: 'Admins',
    test: 'Test Email (to myself)',
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Marketing Email Tool</CardTitle>
        <CardDescription>
          Create and send a marketing email to your users using a branded template.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3 rounded-md border p-3">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <FilePlus2 className="h-4 w-4" /> Saved Emails
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="templateName">Name</Label>
              <Input
                id="templateName"
                placeholder="e.g. Winter Promo v1"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label>Saved Emails</Label>
              <Select
                value={selectedTemplateId}
                onValueChange={setSelectedTemplateId}
                disabled={isLoading || templates.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a saved email" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap items-center gap-2 justify-start md:justify-end md:col-span-2">
              <Button type="button" onClick={handleSaveTemplate} disabled={isLoading}>
                <Save className="mr-2 h-4 w-4" /> Save New
              </Button>
              <Button type="button" variant="secondary" onClick={handleUpdateTemplate} disabled={isLoading || !selectedTemplateId}>
                Update
              </Button>
              <Button type="button" variant="destructive" onClick={handleDeleteTemplate} disabled={isLoading || !selectedTemplateId}>
                <Trash2 className="mr-2 h-4 w-4" /> Delete
              </Button>
              <Button type="button" variant="outline" onClick={applyTemplateToForm} disabled={!selectedTemplateId}>
                Load
              </Button>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Target Audience</Label>
          <RadioGroup value={audience} onValueChange={setAudience} className="flex flex-wrap gap-4">
            {Object.entries(audienceLabels).map(([key, label]) => (
              <div className="flex items-center space-x-2" key={key}>
                <RadioGroupItem value={key} id={`audience-${key}`} disabled={key === 'test' && !profile?.email} />
                <Label htmlFor={`audience-${key}`}>{label}</Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="subject">Email Subject</Label>
              <Input
                id="subject"
                placeholder="e.g. New Feature Announcement!"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="headline">Headline</Label>
              <Input
                id="headline"
                placeholder="e.g. A Better Way to Build"
                value={headline}
                onChange={(e) => setHeadline(e.target.value)}
                disabled={isLoading}
              />
            </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="headerImageUrl">Header Image</Label>
          <div className="flex items-center space-x-2">
            <Input
              id="headerImageUrl"
              placeholder="Enter image URL or upload below"
              value={headerImageUrl}
              onChange={(e) => setHeaderImageUrl(e.target.value)}
              disabled={isLoading || uploadingImage}
            />
            <Button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading || uploadingImage}
            >
              <UploadCloud className="mr-2 h-4 w-4" /> Select File
            </Button>
          </div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
            accept="image/*"
          />
          {selectedFile && (
            <div className="flex items-center space-x-2 mt-2">
              <span className="text-sm text-muted-foreground">{selectedFile.name}</span>
              <Button
                type="button"
                onClick={handleUploadImage}
                disabled={uploadingImage || isLoading}
                size="sm"
              >
                {uploadingImage ? <Loader2 className="animate-spin mr-2" /> : null}
                Upload
              </Button>
            </div>
          )}
          {headerImageUrl && (
            <div className="mt-4">
              <img src={headerImageUrl} alt="Header Preview" className="max-w-full h-auto rounded-md border border-input" />
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="bodyContent">Email Body</Label>
          <RichTextEditor
            value={bodyContent}
            onChange={setBodyContent}
            disabled={isLoading}
            placeholder="Start writing your email content here..."
          />
          <p className="text-xs text-muted-foreground pt-1">
            You can use <code className="bg-muted text-muted-foreground px-1 py-0.5 rounded-sm">[First Name]</code> as a placeholder for the user's first name.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="blockedEmails">Exclude Emails (comma or newline separated)</Label>
          <Textarea
            id="blockedEmails"
            placeholder="user1@example.com, user2@domain.com"
            value={blockedEmailsText}
            onChange={(e) => setBlockedEmailsText(e.target.value)}
            disabled={isLoading}
            className="min-h-24"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="ctaText">Button Text (Optional)</Label>
              <Input
                id="ctaText"
                placeholder="e.g. Learn More"
                value={ctaText}
                onChange={(e) => setCtaText(e.target.value)}
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ctaUrl">Button URL (Optional)</Label>              
              <Input
                id="ctaUrl"
                placeholder="https://canxp.ai/features"
                value={ctaUrl}
                onChange={(e) => setCtaUrl(e.target.value)}
                disabled={isLoading}
              />
            </div>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button disabled={isLoading || !subject || !headline || !bodyContent}>
                {isLoading ? <Loader2 className="animate-spin mr-2" /> : null}
                Send Email
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will send an email to <span className="font-bold">{audienceLabels[audience]}</span>. This action cannot be undone. Please double-check the content.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleSendEmails}>
                  Yes, Send Email
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

      </CardContent>
    </Card>
  );
};

export default MarketingSettings;